import React, { useState, useEffect, useMemo } from "react";
import { MdFileDownload } from "react-icons/md";
import * as S from "./styles";

interface Etapa {
  codigo: string;
  nome: string;
}

interface ModalExportarKanbanProps {
  open: boolean;
  onClose: () => void;
  onExport: (codigosSelecionados: string[]) => void;
  etapas: Etapa[];
  etapasQuantidade: Record<string, number>;
  titulo: string;
  isExporting?: boolean;
}

const ModalExportarKanban: React.FC<ModalExportarKanbanProps> = ({
  open,
  onClose,
  onExport,
  etapas,
  etapasQuantidade,
  titulo,
  isExporting = false,
}) => {
  const [selectedCodigos, setSelectedCodigos] = useState<string[]>([]);

  const etapasLoaded = etapas.length > 0;

  const etapasComItens = useMemo(
    () => etapas.filter((e) => (etapasQuantidade[e.codigo] || 0) > 0),
    [etapas, etapasQuantidade]
  );

  const hasQuantidadeData = useMemo(
    () => etapas.every((e) => e.codigo in etapasQuantidade),
    [etapas, etapasQuantidade]
  );

  // Ao abrir o modal, pré-seleciona apenas etapas com itens
  useEffect(() => {
    if (open && etapasLoaded) {
      setSelectedCodigos(etapasComItens.map((e) => e.codigo));
    }
  }, [open, etapasLoaded, etapasComItens]);

  const allSelectableSelected = useMemo(
    () =>
      etapasComItens.length > 0 &&
      etapasComItens.every((e) => selectedCodigos.includes(e.codigo)),
    [selectedCodigos, etapasComItens]
  );

  const isEtapaDisabled = (codigo: string) =>
    (etapasQuantidade[codigo] || 0) === 0;

  const handleToggle = (codigo: string) => {
    if (isEtapaDisabled(codigo)) return;
    setSelectedCodigos((prev) =>
      prev.includes(codigo)
        ? prev.filter((c) => c !== codigo)
        : [...prev, codigo]
    );
  };

  const handleToggleAll = () => {
    if (allSelectableSelected) {
      setSelectedCodigos([]);
    } else {
      setSelectedCodigos(etapasComItens.map((e) => e.codigo));
    }
  };

  const handleExport = () => {
    onExport(selectedCodigos);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isExporting) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <S.Overlay onClick={handleOverlayClick}>
      <S.ModalContainer>
        <S.ModalHeader>
          <S.ModalTitle>{titulo}</S.ModalTitle>
          <S.ModalDescription>
            A exportação incluirá apenas as propostas que você está visualizando
            no funil no momento.
          </S.ModalDescription>
        </S.ModalHeader>
        <S.ModalBody>
          <S.CheckboxItem disabled={etapasComItens.length === 0}>
            <input
              type="checkbox"
              checked={allSelectableSelected}
              onChange={handleToggleAll}
              disabled={etapasComItens.length === 0}
            />
            <S.CheckboxLabel>Todos</S.CheckboxLabel>
          </S.CheckboxItem>
          {etapas.map((etapa) => {
            const disabled = isEtapaDisabled(etapa.codigo);
            return (
              <S.CheckboxItem key={etapa.codigo} disabled={disabled}>
                <input
                  type="checkbox"
                  checked={selectedCodigos.includes(etapa.codigo)}
                  onChange={() => handleToggle(etapa.codigo)}
                  disabled={disabled}
                />
                <S.CheckboxLabel>{etapa.nome}</S.CheckboxLabel>
              </S.CheckboxItem>
            );
          })}
        </S.ModalBody>
        <S.ModalFooter>
          <S.ExportButton
            onClick={handleExport}
            disabled={
              selectedCodigos.length === 0 || !hasQuantidadeData || isExporting
            }
          >
            <MdFileDownload size={18} />
            {isExporting ? "Exportando..." : "Exportar"}
          </S.ExportButton>
        </S.ModalFooter>
      </S.ModalContainer>
    </S.Overlay>
  );
};

export default ModalExportarKanban;
